import { useState, useEffect, useCallback } from 'react'
import { fmtFecha } from '../utils/fecha'
import api from '../api/client'
import Modal from '../components/Modal'
import Boton from '../components/Boton'

const ESTADO_CHIP = {
  nueva:        { label: 'Nueva',         bg: '#eff6ff', color: '#1d4ed8' },
  en_discusion: { label: 'En discusión',  bg: '#fef9c3', color: '#a16207' },
  aceptada:     { label: 'Aceptada',      bg: '#dcfce7', color: '#166534' },
  implementada: { label: 'Implementada',  bg: '#f0fdf4', color: '#15803d' },
  descartada:   { label: 'Descartada',    bg: '#f1f5f9', color: '#64748b' },
}
const AREAS = ['ventas', 'logistica', 'finanzas', 'marketing', 'sistema', 'general']

/**
 * Bandeja de sugerencias de los usuarios — las captura el asistente IA
 * (widget 💬 / bot Telegram) cuando un empleado propone una mejora desde
 * su experiencia de uso. Acá se discuten, se responde y se marca el destino.
 */
export default function SugerenciasPage() {
  const [data, setData] = useState({ data: [], resumen: {} })
  // Se puede abrir ya filtrada (?estado=nueva — el link del Dashboard, 15 oct 2026).
  const [estado, setEstado] = useState(() => new URLSearchParams(window.location.search).get('estado') || '')
  const [area, setArea] = useState('')
  const [detalle, setDetalle] = useState(null) // sugerencia abierta en modal
  const [respuesta, setRespuesta] = useState('')
  const [saving, setSaving] = useState(false)
  // Carga directa (20 oct 2026): el texto completo que la persona armó con su
  // Claude, sin pasar por el bot (que lo resume).
  const [nueva, setNueva] = useState(null) // { area, titulo, detalle } con el modal abierto
  const [errorNueva, setErrorNueva] = useState('')

  const cargar = useCallback(async () => {
    const r = await api.get('/sugerencias', { params: { estado: estado || undefined, area: area || undefined } })
    setData(r.data)
  }, [estado, area])

  useEffect(() => { cargar() }, [cargar])

  const abrir = (s) => { setDetalle(s); setRespuesta(s.respuesta || '') }

  const guardar = async (nuevoEstado) => {
    setSaving(true)
    try {
      await api.put(`/sugerencias/${detalle.id}`, {
        estado: nuevoEstado || detalle.estado,
        respuesta: respuesta || null,
      })
      setDetalle(null); cargar()
    } finally { setSaving(false) }
  }

  const crear = async () => {
    setSaving(true); setErrorNueva('')
    try {
      await api.post('/sugerencias', nueva)
      setNueva(null); cargar()
    } catch (e) {
      const errs = e.response?.data?.errors
      setErrorNueva(errs ? Object.values(errs).flat().join(' ') : 'No se pudo guardar la sugerencia.')
    } finally { setSaving(false) }
  }

  const rows = data.data || []
  const resumen = data.resumen || {}

  return (
    <div>
      <div style={st.head}>
        <div>
          <h2 style={st.title}>Sugerencias del equipo</h2>
          <p style={st.sub}>Lo que los usuarios proponen desde su experiencia de uso: por el asistente IA o cargadas acá con el texto completo. Tu respuesta les llega cuando preguntan "¿cómo van mis sugerencias?".</p>
        </div>
        <Boton variante="primary" sm onClick={() => { setErrorNueva(''); setNueva({ area: 'general', titulo: '', detalle: '' }) }}>＋ Nueva sugerencia</Boton>
      </div>

      <div style={st.kpis}>
        {Object.entries(ESTADO_CHIP).map(([k, v]) => (
          <button key={k} style={{ ...st.kpi, outline: estado === k ? `2px solid ${v.color}` : 'none' }}
            onClick={() => setEstado(estado === k ? '' : k)}>
            <p style={{ ...st.kpiV, color: v.color }}>{resumen[k] || 0}</p>
            <p style={st.kpiL}>{v.label}</p>
          </button>
        ))}
        <select style={st.select} value={area} onChange={e => setArea(e.target.value)}>
          <option value="">Todas las áreas</option>
          {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead><tr>{['#', 'Fecha', 'Usuario', 'Área', 'Sugerencia', 'Estado', 'Respuesta'].map(h => <th key={h} style={st.th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(s => {
              const chip = ESTADO_CHIP[s.estado] || ESTADO_CHIP.nueva
              return (
                <tr key={s.id} style={st.tr} title="Click para revisar y responder" onClick={() => abrir(s)}>
                  <td style={st.tdId}>#{s.id}</td>
                  <td style={{ ...st.td, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtFecha(s.created_at)}</td>
                  <td style={st.td}>{s.usuario?.nombre || '—'}</td>
                  <td style={st.td}>{s.area}</td>
                  <td style={{ ...st.td, maxWidth: 380 }}>
                    <span style={{ fontWeight: 600 }}>{s.titulo}</span>
                    <span style={st.detalleSub}> — {s.detalle?.slice(0, 80)}{s.detalle?.length > 80 ? '…' : ''}</span>
                  </td>
                  <td style={st.td}><span style={{ ...st.chip, background: chip.bg, color: chip.color }}>{chip.label}</span></td>
                  <td style={{ ...st.td, color: '#64748b' }}>{[(s.adjuntos?.length ? '📎' : ''), (s.respuesta ? '💬' : '')].filter(Boolean).join(' ')}</td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr><td colSpan={7} style={{ ...st.td, textAlign: 'center', color: '#94a3b8', padding: 28 }}>
                Sin sugerencias{estado || area ? ' con esos filtros' : ' todavía — van a aparecer cuando los usuarios le propongan mejoras al asistente 💬'}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detalle && (
        <Modal title={`Sugerencia #${detalle.id} — ${detalle.usuario?.nombre || ''}`} onClose={() => setDetalle(null)} maxWidth={860}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <p style={st.dTitulo}>{detalle.titulo}</p>
              <p style={st.dMeta}>{detalle.area} · {fmtFecha(detalle.created_at)}</p>
              <p style={st.dDetalle}>{detalle.detalle}</p>
              {/* Capturas de pantalla adjuntas (sug #54) — se abren en otra
                  pestaña vía blob (el <img> directo no manda el bearer token). */}
              {(detalle.adjuntos || []).length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {detalle.adjuntos.map((a, i) => (
                    <Boton key={i} variante="secondary" sm onClick={async () => {
                      const { data } = await api.get(`/sugerencias/${detalle.id}/adjuntos/${i}`, { responseType: 'blob' })
                      window.open(URL.createObjectURL(data), '_blank')
                    }}>
                      🖼 Captura {i + 1}
                    </Boton>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label style={st.lbl}>Respuesta al usuario (la ve por el asistente)</label>
              <textarea style={{ ...st.inp, minHeight: 70, resize: 'vertical' }} value={respuesta}
                onChange={e => setRespuesta(e.target.value)} placeholder="ej: Buena idea — lo sumamos a la próxima iteración de la pantalla de remitos." />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <Boton variante="secondary" sm disabled={saving} onClick={() => guardar('en_discusion')}>💬 En discusión</Boton>
                <Boton variante="success" sm disabled={saving} onClick={() => guardar('aceptada')}>✓ Aceptar</Boton>
                <Boton variante="send" sm disabled={saving} onClick={() => guardar('implementada')}>★ Implementada</Boton>
                <Boton variante="destructive" sm disabled={saving} onClick={() => guardar('descartada')}>Descartar</Boton>
              </div>
              <Boton variante="primary" sm disabled={saving} onClick={() => guardar()}>{saving ? 'Guardando…' : 'Guardar respuesta'}</Boton>
            </div>
          </div>
        </Modal>
      )}

      {nueva && (
        <Modal title="Nueva sugerencia" onClose={() => setNueva(null)} maxWidth={860}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ width: 170 }}>
                <label style={st.lbl}>Área</label>
                <select style={st.inp} value={nueva.area} onChange={e => setNueva({ ...nueva, area: e.target.value })}>
                  {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={st.lbl}>Título</label>
                <input style={st.inp} maxLength={200} value={nueva.titulo} autoFocus
                  onChange={e => setNueva({ ...nueva, titulo: e.target.value })}
                  placeholder="ej: Andreani: armar bultos según tamaño de botella" />
              </div>
            </div>
            <div>
              <label style={st.lbl}>Detalle completo — pegá acá el texto tal cual lo armaste (se guarda entero, sin resumir)</label>
              <textarea style={{ ...st.inp, minHeight: 360, resize: 'vertical', fontSize: '0.85rem', lineHeight: 1.45 }}
                value={nueva.detalle} onChange={e => setNueva({ ...nueva, detalle: e.target.value })}
                placeholder="El problema, el caso que lo origina, dónde está en el sistema, la propuesta, las decisiones pendientes, cómo comprobar que quedó bien…" />
            </div>
            {errorNueva && <p style={st.error}>{errorNueva}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Boton variante="secondary" sm disabled={saving} onClick={() => setNueva(null)}>Cancelar</Boton>
              <Boton variante="primary" sm disabled={saving || !nueva.titulo.trim() || !nueva.detalle.trim()} onClick={crear}>
                {saving ? 'Guardando…' : 'Guardar sugerencia'}
              </Boton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

const st = {
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { margin: 0, fontSize: '1.4rem', color: '#0f172a' },
  sub: { margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8', maxWidth: 640 },
  kpis: { display: 'flex', gap: 10, marginBottom: 16, alignItems: 'stretch', flexWrap: 'wrap' },
  kpi: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 16px', textAlign: 'center', cursor: 'pointer' },
  kpiV: { margin: 0, fontSize: '1.3rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  kpiL: { margin: '2px 0 0', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  select: { marginLeft: 'auto', padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem', background: '#fff', alignSelf: 'center' },
  tableWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },
  td: { padding: '10px 12px', color: '#0f172a', verticalAlign: 'top' },
  tdId: { padding: '10px 12px', color: '#64748b', verticalAlign: 'top', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' },
  detalleSub: { color: '#94a3b8', fontSize: '0.82rem' },
  chip: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap' },
  dTitulo: { margin: 0, fontWeight: 700, color: '#0f172a' },
  dMeta: { margin: '2px 0 8px', fontSize: '0.78rem', color: '#94a3b8' },
  dDetalle: { margin: 0, fontSize: '0.9rem', color: '#374151', whiteSpace: 'pre-wrap', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, maxHeight: '55vh', overflowY: 'auto' },
  error: { margin: 0, fontSize: '0.82rem', color: '#b91c1c' },
  lbl: { display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 },
  inp: { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'inherit' },
}
